// ============================================================================
// /people — the Desk: what is waiting on HR, as a queue, not totals (Ben,
// 2026-10-07, item 818). Replaces the People hub's generic Overview and the HR
// dashboard's count tiles for HR, the executives and superusers.
//
// Four lanes, every row opens the record it is about:
//   Needs info — hourly workers with no phone (one row → the directory's No
//                phone filter) · subs with live buildings and no valid
//                insurance certificate (one row → the hub's Compliance tab) ·
//                new hires whose checklist has nothing done
//   Starting   — onboarding in progress, any type
//   Hiring     — Open, Interview and On hold requests, oldest first by default
//   Leaving    — open separations; hidden when there are none
//
// No new data and no new table: every lane reads the system of record its
// screen already reads (hiring_requests, the onboarding union, the people
// directory view, the compliance view, employee_separations), through the
// same hooks those screens use. The counts here are therefore the counts
// those screens show. Home carries none of these (measured 2026-10-08: Home
// reads building requirements and My Work rows, not HR's queue), so there is
// no second number for them to disagree with.
//
// Each person arranges the lanes their own way through the shared board view
// control — pins, their order — saved per person, per board.
// ============================================================================
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DndContext, PointerSensor, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { ArrowRight, ClipboardCheck, PhoneOff, ShieldAlert, UserMinus, UserPlus } from 'lucide-react';
import { Card, ErrorState, PageHeader, Skeleton } from '../../shared/components';
import {
  arrangeCards,
  BoardCard,
  BoardViewControls,
  boardCollision,
  resolveBoardDrop,
  useBoardViewPrefs,
  type BoardViewApi,
} from '../../shared/board-view';
import { useHiringRequests, type HiringRequestRow } from '../hr-hiring/hooks/use-hiring-data';
import { useOnboardingRecords } from '../hr-onboarding/hooks/use-onboarding-data';
import { useWorkingSubInsurance } from '../hr-onboarding/hooks/use-working-sub-insurance';
import { allVisibleItems, effectiveChecklist } from '../hr-onboarding/utils';
import type { OnboardingRecord } from '../hr-onboarding/types';
import { useSeparationList } from '../hr-separation/hooks/use-separation-data';
import { isNoPhoneWorker, usePeopleDirectory } from './hooks/use-people-directory';

type LaneId = 'needs_info' | 'starting' | 'hiring' | 'leaving';

/** One row on the Desk. `id` is unique across lanes (lane-prefixed). */
interface DeskRow {
  id: string;
  lane: LaneId;
  to: string;
  title: string;
  detail: string;
  count?: number;
  createdAt: string | null;
  startDate?: string | null;
  tone?: 'warn' | 'danger' | 'default';
}

const LANES: ReadonlyArray<{ id: LaneId; label: string; hint: string; icon: typeof UserPlus }> = [
  { id: 'needs_info', label: 'Needs info', hint: 'Something is missing before work can go on.', icon: ShieldAlert },
  { id: 'starting', label: 'Starting', hint: 'Onboarding in progress, any type.', icon: ClipboardCheck },
  { id: 'hiring', label: 'Hiring', hint: 'Open, interviewing and on hold. Oldest first.', icon: UserPlus },
  { id: 'leaving', label: 'Leaving', hint: 'Open separations.', icon: UserMinus },
];

/** The hiring statuses the Desk calls open — the board's live columns. */
export const DESK_HIRING_STATUSES: readonly string[] = ['Open', 'Interview', 'On_Hold'];

/** A new hire whose checklist has nothing done yet. */
export function nothingDone(rec: OnboardingRecord): boolean {
  if (rec.terminal || rec.source !== 'employee') return false;
  const cl = effectiveChecklist(rec.checklist);
  const items = allVisibleItems(cl, rec.employmentTypeForChecklist).map((x) => x.item);
  return items.length > 0 && !items.some((it) => it.status === 'complete');
}

function fmtAge(iso: string | null): string {
  if (!iso) return '';
  const days = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 86_400_000));
  return days === 0 ? 'today' : days === 1 ? '1 day' : `${days} days`;
}

export default function PeopleDesk() {
  const dir = usePeopleDirectory();
  const hiring = useHiringRequests();
  const onboarding = useOnboardingRecords();
  const insurance = useWorkingSubInsurance();
  const seps = useSeparationList();
  const view = useBoardViewPrefs('desk');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const loading = dir.loading || hiring.isLoading || onboarding.loading || insurance.loading || seps.loading;
  const error = dir.error ?? hiring.error ?? onboarding.error ?? insurance.error ?? seps.error;

  const rows = useMemo<DeskRow[]>(() => {
    const out: DeskRow[] = [];

    // ----- Needs info -----
    const noPhone = dir.rows.filter(isNoPhoneWorker).length;
    if (noPhone > 0) {
      out.push({
        id: 'needs:phones',
        lane: 'needs_info',
        to: '/people/directory?filter=no_phone',
        title: `${noPhone} hourly ${noPhone === 1 ? 'worker has' : 'workers have'} no mobile number`,
        detail: 'No clock-in code can be issued until a number is on their card.',
        count: noPhone,
        createdAt: null,
        tone: 'warn',
      });
    }
    const uninsured = insurance.rows.filter((r) => r.coi_severity <= 1);
    if (uninsured.length > 0) {
      out.push({
        id: 'needs:coi',
        lane: 'needs_info',
        to: '/people/subcontractors?view=compliance&tab=insurance',
        title: `${uninsured.length} ${uninsured.length === 1 ? 'sub' : 'subs'} with live buildings and no valid insurance certificate`,
        detail: uninsured
          .slice(0, 3)
          .map((r) => r.person ?? r.display_name)
          .join(' · ') + (uninsured.length > 3 ? ' · …' : ''),
        count: uninsured.length,
        createdAt: null,
        tone: 'danger',
      });
    }
    for (const rec of onboarding.records.filter(nothingDone)) {
      out.push({
        id: `needs:hire:${rec.source}:${rec.onboarding_id}`,
        lane: 'needs_info',
        to: `/people/onboarding?open=${encodeURIComponent(`${rec.source}:${rec.onboarding_id}`)}`,
        title: `${rec.name} — nothing on the checklist done`,
        detail: [rec.role, rec.account, rec.start_date ? `starts ${rec.start_date}` : null].filter(Boolean).join(' · '),
        createdAt: rec.created_at,
        startDate: rec.start_date,
        tone: 'warn',
      });
    }

    // ----- Starting -----
    for (const rec of onboarding.records.filter((r) => !r.terminal)) {
      out.push({
        id: `starting:${rec.source}:${rec.onboarding_id}`,
        lane: 'starting',
        to: `/people/onboarding?open=${encodeURIComponent(`${rec.source}:${rec.onboarding_id}`)}`,
        title: rec.name,
        detail: [rec.type, rec.stage.replace(/_/g, ' '), rec.account, rec.start_date ? `starts ${rec.start_date}` : null]
          .filter(Boolean)
          .join(' · '),
        createdAt: rec.created_at,
        startDate: rec.start_date,
      });
    }

    // ----- Hiring — oldest first by default -----
    const open = hiring.data
      .filter((r: HiringRequestRow) => DESK_HIRING_STATUSES.includes(r.status ?? ''))
      .sort((a, b) => Date.parse(a.created_at ?? '') - Date.parse(b.created_at ?? ''));
    for (const r of open) {
      out.push({
        id: `hiring:${r.request_id}`,
        lane: 'hiring',
        to: `/people/hiring?open=${encodeURIComponent(r.request_id)}`,
        title: r.title ?? r.position_title ?? r.request_id,
        detail: [r.facility_name ?? r.account_name, (r.status ?? '').replace(/_/g, ' '), r.urgency_label, `open ${fmtAge(r.created_at)}`]
          .filter(Boolean)
          .join(' · '),
        createdAt: r.created_at,
        startDate: r.target_start_date,
      });
    }

    // ----- Leaving -----
    for (const s of seps.rows.filter((r) => r.status !== 'Completed')) {
      out.push({
        id: `leaving:${s.separation_id}`,
        lane: 'leaving',
        to: `/people/separations/${s.separation_id}`,
        title: s.employee_name ?? s.employee_id,
        detail: [s.separation_type, s.last_working_day ? `last day ${s.last_working_day}` : null, `${s.open_items} open items`]
          .filter(Boolean)
          .join(' · '),
        createdAt: (s as { created_at?: string | null }).created_at ?? null,
      });
    }
    return out;
  }, [dir.rows, hiring.data, onboarding.records, insurance.rows, seps.rows]);

  const lanes = useMemo(
    () =>
      LANES.map((lane) => ({
        ...lane,
        rows: arrangeCards(
          rows.filter((r) => r.lane === lane.id),
          (r) => ({ id: r.id, createdAt: r.createdAt, startDate: r.startDate ?? null }),
          view.prefs,
        ),
      })).filter((lane) => lane.id !== 'leaving' || lane.rows.length > 0),
    [rows, view.prefs],
  );

  function onDragEnd(e: DragEndEvent) {
    if (!e.over) return;
    const row = rows.find((r) => r.id === String(e.active.id));
    if (!row) return;
    const drop = resolveBoardDrop(e.over.id, e.over.data.current as { columnId?: string } | undefined, row.lane);
    if (drop.kind !== 'reorder') return; // a Desk row never changes lane by hand
    const shown = lanes.find((l) => l.id === row.lane)?.rows.map((r) => r.id) ?? [];
    view.place(shown, row.id, drop.overCardId);
  }

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 md:p-6">
      <PageHeader
        eyebrow="People · the Desk"
        title="Desk"
        meta={loading ? 'Loading…' : `${rows.length} things waiting · every row opens its record`}
      />
      {error && <ErrorState title="Part of the Desk did not load" body={error} />}
      <BoardViewControls view={view} hasStartDate />
      {loading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={boardCollision} onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {lanes.map((lane) => (
              <Lane key={lane.id} id={lane.id} label={lane.label} hint={lane.hint} Icon={lane.icon} rows={lane.rows} view={view} />
            ))}
          </div>
        </DndContext>
      )}
    </div>
  );
}

function Lane({
  id,
  label,
  hint,
  Icon,
  rows,
  view,
}: {
  id: LaneId;
  label: string;
  hint: string;
  Icon: typeof UserPlus;
  rows: DeskRow[];
  view: BoardViewApi;
}) {
  const { setNodeRef } = useDroppable({ id });
  const shownIds = rows.map((r) => r.id);
  return (
    <section ref={setNodeRef} className="min-w-0" aria-label={label}>
      <h2 className="mb-2 flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
        <Icon className="h-3.5 w-3.5" />
        <span>{label}</span>
        <span className="ml-auto rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] text-tcs-text-secondary">{rows.length}</span>
      </h2>
      <p className="mb-2 px-1 text-[11px] text-tcs-text-muted">{hint}</p>
      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-tcs-border-strong bg-tcs-raised p-4 text-center text-xs text-tcs-text-muted">
          Nothing waiting.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <BoardCard key={r.id} id={r.id} columnId={id} shownIds={shownIds} view={view} draggable as="li">
              <DeskRowView row={r} />
            </BoardCard>
          ))}
        </ul>
      )}
    </section>
  );
}

function DeskRowView({ row }: { row: DeskRow }) {
  const tone =
    row.tone === 'danger'
      ? 'border-st-danger-border'
      : row.tone === 'warn'
        ? 'border-st-warn-border'
        : 'border-tcs-border';
  return (
    <Card className={`p-0 ${tone}`}>
      <Link to={row.to} className="block px-3 py-2.5 pr-20 hover:bg-tcs-surface">
        <span className="flex items-start gap-2">
          {row.id === 'needs:phones' && <PhoneOff className="mt-0.5 h-4 w-4 shrink-0 text-st-warn-fg" />}
          {row.id === 'needs:coi' && <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-st-danger-fg" />}
          <span className="min-w-0">
            <span className="block text-sm font-medium text-tcs-text">{row.title}</span>
            {row.detail && <span className="mt-0.5 block text-xs text-tcs-text-secondary">{row.detail}</span>}
          </span>
        </span>
        <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-tcs-accent">
          Open <ArrowRight className="h-3 w-3" />
        </span>
      </Link>
    </Card>
  );
}
